<script setup lang="ts">
import { Roles } from '@/constants'
import directoriesHttpService from '@/services/http/directoriesHttpService'
import dictionariesHttpService from '@/services/http/dictionariesHttpService'
import type { DirectionModel } from '@/services/http/httpResponses'
import { ref } from 'vue'

definePage({
  meta: { allowedRoles: [Roles.Admin] },
})

const EDUCATION_LEVEL_SEVERITY: Record<
  string,
  'info' | 'success' | 'warn' | 'secondary' | 'contrast'
> = {
  Бакалавриат: 'info',
  Магистратура: 'success',
  Докторантура: 'contrast',
  ТиПО: 'secondary',
}

const items = ref<DirectionModel[]>([])
const totalCount = ref(0)
const loading = ref(false)
const search = ref('')
const page = ref(1)
const PAGE_SIZE = 50

const languagesResponse = await dictionariesHttpService.getSupportedLanguages()
const languages = ref(languagesResponse.languages)

const load = async () => {
  loading.value = true
  try {
    const res = await directoriesHttpService.getDirections({
      search: search.value || undefined,
      page: page.value,
      pageSize: PAGE_SIZE,
    })
    items.value = res.items
    totalCount.value = res.totalCount
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  page.value = 1
  load()
}

const onReset = () => {
  search.value = ''
  onSearch()
}

const onPage = (event: { page: number }) => {
  page.value = event.page + 1
  load()
}

await load()
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-semibold">Направления</h2>
      <span class="text-gray-500 text-sm">Всего: {{ totalCount }}</span>
    </div>

    <div class="flex gap-2">
      <InputText
        v-model="search"
        placeholder="Поиск по коду или названию"
        class="w-80"
        @keyup.enter="onSearch"
      />
      <Button label="Найти" @click="onSearch" />
      <Button v-if="search" label="Сбросить" severity="secondary" text @click="onReset" />
    </div>

    <DataTable :value="items" :loading="loading" dataKey="id" class="w-full">
      <Column field="code" header="Код" style="width: 150px" />
      <Column header="Уровень" style="width: 140px">
        <template #body="{ data }">
          <Tag
            :value="data.educationLevel"
            :severity="EDUCATION_LEVEL_SEVERITY[data.educationLevel] ?? 'secondary'"
          />
        </template>
      </Column>
      <Column
        v-for="lang in languages"
        :key="lang.code"
        :header="`${lang.name} (${lang.code})`"
        style="min-width: 200px"
      >
        <template #body="{ data }">
          {{
            (data as DirectionModel).translations.find((t) => t.languageCode === lang.code)
              ?.value ?? ''
          }}
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center py-4">
          <span class="pi pi-list text-6xl text-gray-300" />
          <p class="text-gray-500">Данные отсутствуют</p>
        </div>
      </template>
    </DataTable>

    <Paginator
      v-if="totalCount > PAGE_SIZE"
      :rows="PAGE_SIZE"
      :totalRecords="totalCount"
      :first="(page - 1) * PAGE_SIZE"
      @page="onPage"
    />
  </section>
</template>

<style scoped></style>
